/**
 * Development Account Middleware
 * 
 * This service provides special access privileges for development accounts.
 * It intercepts authentication and subscription checks to grant full access
 * to specified development email addresses.
 */

// List of development accounts that should have full access
const DEV_ACCOUNTS = [
  'stackcurious@gmail.com'
];

// Check if an email is a development account
export const isDevAccount = (email: string | null): boolean => {
  if (!email) return false;
  return DEV_ACCOUNTS.includes(email.toLowerCase());
};

// Override user subscription tier for dev accounts
export const getEffectiveTier = (email: string | null, currentTier: string | null | undefined): string => {
  if (isDevAccount(email)) {
    // Dev accounts always get the highest tier with all features
    return 'pro';
  }
  
  // Normal users get their assigned tier
  return currentTier || 'free';
};

// Check if a user has access to pro features
export const hasProAccess = (email: string | null, tier: string | null | undefined): boolean => {
  // Dev accounts always have pro access
  if (isDevAccount(email)) {
    return true;
  }
  
  // Regular users need a pro tier
  return tier === 'pro';
};

// Check if a user has access to command center features
export const hasCommandCenterAccess = (email: string | null, addons: string[] | null | undefined): boolean => {
  // Dev accounts always have command center access
  if (isDevAccount(email)) {
    return true;
  }
  
  // Regular users need the command-center addon
  return Array.isArray(addons) && addons.includes('command-center');
};

// For use with API responses that require pro subscription
export const enhanceApiResponse = <T extends object>(
  email: string | null,
  response: T & { error?: string, code?: string }
): T => {
  // If this is a dev account and the error is 'PRO_REQUIRED',
  // remove the error and provide mock data access
  if (isDevAccount(email) && response.error && response.code === 'PRO_REQUIRED') {
    const enhancedResponse = { ...response };
    delete enhancedResponse.error;
    delete enhancedResponse.code;
    return enhancedResponse;
  }
  
  return response;
};

// Reset onboarding for dev accounts (for testing)
export const resetDevOnboarding = (email: string | null): void => {
  if (isDevAccount(email)) {
    localStorage.removeItem('hasCompletedOnboarding');
    // Optionally reload to trigger onboarding flow
    window.location.href = '/auth';
  }
};

// Add to window for easy access in console during dev
if (typeof window !== 'undefined') {
  (window as any).resetDevOnboarding = resetDevOnboarding;
}